// ZKX Wallet — Home v2 SHARED design tokens.
//
// Screen-agnostic visual primitives promoted out of
// `src/mobile/modules/dashboard/screens/home/tokens.ts` so BOTH the mobile-web
// home screen AND the extension popup/tab dashboard consume one source.
//
// Layered on top of the shared theme token system (`useTheme().theme`).
// This module only adds what the theme does not express: corner radii, the
// public/private gradient surfaces, responsive breakpoints and a few helpers
// that resolve a mode-dependent accent from the existing theme tokens.
//
// Keep semantic colors (text, backgrounds, borders, deltas) coming from
// `theme` so light/dark and future palette changes stay centralized.
//
// `ModeEnum`/`Mode` are re-used from the single canonical source
// (`@web/contexts/appModeContext`) — the same enum the extension dashboard and
// the mobile home screen already import; do not create a second one.
import { LinearGradientProps } from 'expo-linear-gradient'

import { ThemeProps } from '@common/styles/themeConfig'
import { Mode, ModeEnum } from '@web/contexts/appModeContext'

export { ModeEnum }
export type { Mode }

// Corner radii — mirror the design's r-sm / r-md / r-lg / r-pill.
export const RADII = {
  sm: 8,
  md: 12,
  lg: 16,
  pill: 100
} as const

// Breakpoints (px), matching the design's responsive behavior:
//  - >= desktop : sidebar + 2-col grid
//  - tablet..   : sidebar + single column (right stacks under left)
//  - < tablet   : bottom tab bar, single column, detail/activity as sheets
export const BREAKPOINTS = {
  desktop: 1200,
  tablet: 768
} as const

// Balance-card gradient surfaces. Public = blue, Private = orange.
// Reused from the Header mode-toggle gradients so the card and the toggle agree.
export const MODE_GRADIENT: Record<Mode, Pick<LinearGradientProps, 'colors' | 'start' | 'end'>> = {
  [ModeEnum.PUBLIC]: {
    colors: ['#5BC7FF', '#0A6CF0', '#0A2E8A'],
    start: { x: 1, y: 1 },
    end: { x: 0, y: 0 }
  },
  [ModeEnum.PRIVATE]: {
    colors: ['#d1a247', '#b08432', '#4E341B'],
    start: { x: 1, y: 1 },
    end: { x: 0, y: 0 }
  }
}

// CSS gradient fills for the active mode-toggle pill. Public = blue, Private = orange.
// A white top-down sheen layered over a radial brand gradient anchored bottom-left.
export const MODE_TOGGLE_GRADIENT_CSS: Record<Mode, string> = {
  [ModeEnum.PUBLIC]:
    'linear-gradient(rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(120% 200% at 0% 100%, rgb(89, 185, 255) 0%, rgb(19, 118, 232) 55%, rgb(10, 63, 168) 100%)',
  [ModeEnum.PRIVATE]:
    'linear-gradient(rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(120% 200% at 0% 100%, rgb(255, 212, 137) 0%, rgb(224, 115, 0) 55%, rgb(111, 46, 0) 100%)'
}

// Grainient (animated grain-gradient) params for the balance-card banner.
// Shared shader params per the design; only the 3 colors swap per mode.
// Public = blue, Private = orange.
export const GRAINIENT_BASE = {
  timeSpeed: 0.65,
  colorBalance: -0.3,
  warpStrength: 1.85,
  warpFrequency: 6.8,
  warpSpeed: 2,
  warpAmplitude: 45,
  blendAngle: 0,
  blendSoftness: 0.05,
  rotationAmount: 500,
  noiseScale: 2,
  grainAmount: 0.06,
  grainScale: 2,
  grainAnimated: true,
  contrast: 1.15,
  gamma: 0.9,
  saturation: 1.25,
  centerX: 0.16,
  centerY: 0,
  zoom: 0.9
} as const

export const GRAINIENT_COLORS: Record<Mode, { color1: string; color2: string; color3: string }> = {
  [ModeEnum.PUBLIC]: { color1: '#43D0FE', color2: '#030F7B', color3: '#43D0FE' },
  [ModeEnum.PRIVATE]: { color1: '#b08432', color2: '#4E341B', color3: '#d1a247' }
}

/** The accent color for the current mode (public = primary blue, private = orange). */
export const getModeAccent = (theme: ThemeProps, mode: Mode): string =>
  (mode === ModeEnum.PRIVATE ? theme.privateAccent : theme.primary) as string

/** Translucent fill for the selected-asset row / accent pill, per mode. */
export const getModeAccentSoft = (theme: ThemeProps, mode: Mode): string =>
  (mode === ModeEnum.PRIVATE ? theme.privateAccent20 : theme.primary20) as string

/** Positive/negative delta colors for 24h price change. */
export const getDeltaColor = (theme: ThemeProps, isUp: boolean): string =>
  (isUp ? theme.successText : theme.errorText) as string
